import * as THREE from 'three';
import { instantiate, CHARACTER_FORWARD_YAW_OFFSET, type GLBInstance, type LoadedGLB } from '../core/assets';
import { MOVE_SPEED } from './controller';
import { WEARABLES, type WearableId, type WearableSlot } from '../economy/wardrobe';

const RUNNER_ASSET_URL = '/assets/models/chr_runner.glb';

type PlayerClip = 'idle' | 'walk' | 'run' | 'jump' | 'slide';

/**
 * C10 wire-in (PLAN-CITY3 §9): `chr_runner.glb` now ships real per-clip
 * authored ground speeds (Codex's report.json, re-verified against the
 * delivered file's own FK data before trusting it) instead of the game
 * always forcing 'run' at its raw authored rate — this retires R-4's
 * interim run-only clamp AND R-3's `IDLE_TIME_SCALE` stretch, since both
 * clips are now authored correctly at the source (run cadence dropped from
 * 9.6 to 5.33 footfalls/s at 4.8 m/s; idle is a real 2.5s breath loop). A
 * genuine `walk` clip exists for the first time, authored at 1.5 m/s.
 * R-2's `EYE_RECOLOR_MAP` is retired too — the face is real eye-white/iris/
 * pupil/brow/hair materials at the source now, not an engine-side recolor.
 */
const WALK_AUTHORED_SPEED = 1.5; // m/s — report.json's measured authored ground speed for 'walk'
const RUN_AUTHORED_SPEED = 4.8; // m/s — matches MOVE_SPEED exactly: timeScale 1.0 at full stick, by construction
/**
 * Below this commanded speed, 'walk' reads better than a slowed-down 'run'
 * (and vice versa above it) — derived from MOVE_SPEED rather than a bare
 * literal so a future MOVE_SPEED tuning session can't silently desync the
 * gait switch from the speed it's actually switching on (the same
 * derive-don't-hand-copy law controller.ts's own MOVE_SPEED/GRAVITY/
 * JUMP_SPEED exports state explicitly — T1/B-1 gotcha family). At this
 * exact boundary walk's timeScale is 2.4/1.5=1.6 and run's is 2.4/4.8=0.5 —
 * symmetric brackets around each clip's own authored speed, not an
 * arbitrary split.
 */
const RUN_SWITCH_SPEED = MOVE_SPEED / 2;
/** Below this, a clip playing at commanded speed reads as slow-motion rather than a genuine slow walk — matches R-4's own interim floor (0.35) rounded up to sit exactly at run's natural floor at the switch boundary (2.4/4.8=0.5), so neither gait has a different "how slow is too slow" ceiling. */
const CLIP_TIMESCALE_FLOOR = 0.5;
/** Walk's natural ceiling is exactly the switch boundary's own ratio (2.4/1.5) — in practice never reached, since walk stops being selected at that speed; kept explicit rather than left as "whatever the unclamped math produces." */
const WALK_TIMESCALE_CEILING = RUN_SWITCH_SPEED / WALK_AUTHORED_SPEED;

/**
 * Review fix (MEDIUM, correctness): `ceiling` for 'run' IS genuinely
 * load-bearing, not a defensive no-op — a prior version of this comment
 * claimed `commandedSpeed` can never exceed `MOVE_SPEED` (making the ratio
 * unreachable above 1.0 "by construction"), which a 3-lens review disproved
 * with a concrete, deterministic counterexample: on the exact tick a slide's
 * `slideTimer` expires, `controller.ts`'s `update()` sets
 * `state.sliding = false` INSIDE the sliding branch, but that same tick's
 * `dirX`/`dirZ`/`speed` locals (and therefore `commandedSpeed`) were already
 * captured from the slide branch — unit direction × `SLIDE_SPEED`
 * (`MOVE_SPEED * 1.85`) — so `commandedSpeed` can read ~8.88 on a tick where
 * `sliding` already reads false. `groundedClipFor` then selects 'run' (well
 * above `RUN_SWITCH_SPEED`) with a raw ratio of ~1.85 — this `ceiling=1`
 * clamp is what silently catches it, producing a frame indistinguishable
 * from a normal full-speed run rather than a visible speed-up. The
 * controller-side root cause (an un-zeroed `commandedSpeed` on the slide's
 * OWN end-of-timer transition, the same class of bug the vaulting branch
 * already fixes for itself at controller.ts's own "Review fix (MEDIUM,
 * spec-fidelity)" comment) is left alone here rather than restructured —
 * there is no player-visible defect to fix (this clamp already fully
 * absorbs it every time), and a real fix needs re-deriving live movement
 * intent within the same tick sliding ends, a bigger change than this
 * wire-in's own scope. Flagged on STATUS-CITY.md rather than silently
 * dropped.
 */
function clipTimeScaleFor(commandedSpeed: number, authoredSpeed: number, ceiling: number): number {
  return THREE.MathUtils.clamp(commandedSpeed / authoredSpeed, CLIP_TIMESCALE_FLOOR, ceiling);
}

/**
 * The grounded, non-sliding/vaulting pose for a given state — factored out
 * so the hysteresis fallback in `update()` can recompute the SAME decision
 * the primary branch made, rather than duplicating the walk/run/idle
 * ternary in two places and risking the two silently drifting apart (the
 * project's own T-formula-drift family, gotcha #19: two independently
 * recomputed expressions for "the same" value WILL disagree eventually).
 */
function groundedClipFor(state: PlayerVisualState): 'walk' | 'run' | 'idle' {
  if (!state.moving) return 'idle';
  return state.speed < RUN_SWITCH_SPEED ? 'walk' : 'run';
}

/**
 * R-3: single-frame `grounded` flicker (a slope) is absorbed by requiring
 * grounded to have read false for at least this many CONSECUTIVE ticks
 * before allowing a switch TO 'jump'. `ungroundedStreak` is already 1 (not
 * 0) on the very first tick grounded reads false, so this threshold=2 costs
 * a real jump's pose transition EXACTLY ONE tick (~16.7ms @60fps) of delay
 * past the physics event — not "a couple frames" (an earlier version of
 * this comment overstated it; a 3-lens review independently hand-traced the
 * actual tick sequence and caught the discrepancy). This is the
 * mathematically necessary minimum for this design to absorb any flicker at
 * all (threshold=1 would never gate anything) and sits exactly at, not
 * inside, Wave R's own "no more than one frame past the physics event"
 * bound — do NOT raise this past 2 to also absorb a 2-tick flicker; that
 * would push the real-jump delay to 2 frames and violate the bound. Real
 * vault/slide transitions bypass this entirely — see the `update()` gate.
 */
const MIN_UNGROUNDED_TICKS_BEFORE_JUMP = 2;

/** The controller-state slice this class needs to pick a clip — a small, explicit shape (not the whole `ControllerState`) so this file doesn't need to import `world/controller.ts` just for a type. */
export interface PlayerVisualState {
  moving: boolean;
  grounded: boolean;
  vaulting: boolean;
  sliding: boolean;
  /** The controller's true commanded speed this tick (m/s) — `world/controller.ts`'s `PlayerCapsule.getCommandedSpeed()`. C10 wire-in: selects 'walk' vs 'run' (see `groundedClipFor`) AND couples the chosen clip's timeScale to it. */
  speed: number;
}

interface EquippedWearable {
  instance: GLBInstance;
  /** The wearable's own `SOCKET_<slot>` Empty, expressed in the wearable's OWN root-local space (not its immediate parent's — computed once via world-position round-trip so it's correct regardless of the delivered GLB's internal node depth). */
  socketLocal: THREE.Vector3;
}

/**
 * Wave L-2 (PLAN-CITY2 §6): wires `chr_runner.glb` as the REAL player
 * visual (idle/walk/run/jump/slide, per the manifest's own clip list) — until
 * this class, the live player was `PlayerCapsule`'s bare orange capsule
 * primitive (`world/controller.ts`), the same "no rig exists yet, ship a
 * gray-box and swap later" discipline every enemy/boss/prop in this game
 * already used before its own Codex asset landed (§0.4). `PlayerCapsule`
 * keeps owning COLLISION unchanged — this class only ever READS its
 * position/facing/state (via `update()`'s params), it never writes back;
 * `main.ts` still hides the capsule's own visible mesh once this class's
 * GLB is ready, exactly like `Enemy`/`Boss` swap from a primitive fallback
 * to a real asset once `setAsset()`/its own async load resolves.
 *
 * Also owns C09's wearable bone-attach (`equip()`): each equipped wearable
 * is parented as a CHILD of `group` (this class's own root, which `update()`
 * sets to the player's live position + facing yaw every tick) — inheriting
 * `group`'s rotation is exactly what "follow the player's facing" means, and
 * crucially is NOT the same as inheriting the runner skeleton's own
 * `SOCKET_head`/`SOCKET_hand_R` empties' baked +90°-about-X export rotation
 * (C09 brief: "the attach code must use anchor POSITIONS only... never
 * compose the wearer socket's rotation, otherwise every head piece arrives
 * pitched 90°"). Each wearable's LOCAL rotation is left at its identity
 * default — it inherits `group`'s yaw and nothing else, by construction.
 */
export class PlayerVisual {
  readonly group = new THREE.Group();
  private instance: GLBInstance | null = null;
  private currentClip: PlayerClip | null = null;
  private currentAction: THREE.AnimationAction | null = null;
  /**
   * R-3: consecutive ticks `state.grounded` has read false, reset to 0 the
   * instant it reads true again. A switch TO 'jump' is held back until this
   * clears MIN_UNGROUNDED_TICKS_BEFORE_JUMP, absorbing a one-frame
   * `grounded` flicker (e.g. descending a slope — controller.ts's own T1 3c
   * comment) that would otherwise snap the pose.
   *
   * Deliberately NOT "seconds spent in the CURRENT clip" (a first draft of
   * this field measured that instead, and self-caught, via a direct
   * `pv.update()` unit probe before this ever reached review): dwell-in-
   * clip keeps growing the longer a player runs, so by the time a real
   * flicker happens after even a couple seconds of running the gate was
   * already open and let it straight through — the gate has to measure how
   * long the FLICKER CONDITION ITSELF has persisted, not how long the
   * current pose has been showing.
   */
  private ungroundedStreak = 0;
  private headSocketNode: THREE.Object3D | null = null;
  private spineBoneNode: THREE.Object3D | null = null;
  private readonly wearableCache = new Map<WearableId, EquippedWearable>();
  private readonly equipped: Partial<Record<WearableSlot, WearableId>> = {};
  private readonly scratchWorld = new THREE.Vector3();

  constructor(scene: THREE.Scene, loadGLB: (url: string) => Promise<LoadedGLB>) {
    scene.add(this.group);
    loadGLB(RUNNER_ASSET_URL).then((glb) => {
      this.instance = instantiate(glb);
      this.group.add(this.instance.group);
      // C10 wire-in: R-2's engine-side six-eyes recolor traverse is retired
      // here — the delivered GLB now ships real eye-white/iris/pupil/brow/
      // hair materials at the source (report.json's own
      // `approved_face_assignments_verified: true`), so `instantiate()`'s
      // per-mesh material clone (core/assets.ts) needs no help from this
      // class to render the face correctly.
      // Cached once at load — C01's own manifest entry (`sockets: ["SOCKET_
      // hand_R","SOCKET_head"]`) confirms SOCKET_head exists; `spine_02` is a
      // real bone in the 18-bone rig (ai/archetypes.ts's own crew-rig
      // convention), not a marker empty, but `getObjectByName` finds either
      // kind identically — a three.js `Bone` is just an `Object3D` subclass.
      this.headSocketNode = this.instance.group.getObjectByName('SOCKET_head') ?? null;
      this.spineBoneNode = this.instance.group.getObjectByName('spine_02') ?? null;
      this.playClip('idle');
    });
  }

  get ready(): boolean {
    return this.instance !== null;
  }

  /**
   * Loads (and caches — safe to call repeatedly) one wearable's asset so it
   * CAN be equipped; does not equip it. `main.ts` calls this once per owned
   * wearable at boot (mirroring the existing `loadGLB(...).then((glb) =>
   * enemyManager.setAsset(...))` idiom every enemy asset already uses), and
   * again the instant a new one is granted (`loot/collect.ts`'s
   * `onCosmeticCollected`) so it's equippable immediately, not just after a
   * reload.
   */
  cacheWearableAsset(id: WearableId, glb: LoadedGLB): void {
    if (this.wearableCache.has(id)) return;
    const instance = instantiate(glb);
    instance.group.updateMatrixWorld(true);
    const socketNode = instance.group.getObjectByName(`SOCKET_${WEARABLES[id].slot}`);
    const socketLocal = new THREE.Vector3();
    if (socketNode) {
      socketNode.getWorldPosition(socketLocal);
      instance.group.worldToLocal(socketLocal); // robust to any internal node depth the delivered GLB happens to use
    }
    // else: no socket node found (a malformed delivery) — socketLocal stays
    // (0,0,0), so the piece attaches at its own mesh origin instead of
    // silently throwing; a wrong-looking equip is recoverable, a crash
    // mid-combat isn't.
    instance.group.visible = false;
    this.wearableCache.set(id, { instance, socketLocal });
  }

  /**
   * `id: null` unequips the slot. A no-op if `id` isn't cached yet
   * (`cacheWearableAsset` hasn't resolved) — the caller (main.ts) re-calls
   * `equip` once loading finishes, same "async catches up" pattern as every
   * other GLB-backed entity in this game.
   *
   * Review fix (MEDIUM, correctness): the cache lookup for the REPLACEMENT
   * now happens BEFORE the current piece is removed — the original order
   * removed the old piece unconditionally, then bailed out if the new one
   * wasn't cached yet, leaving `this.equipped[slot]` internally stale (still
   * pointing at the just-removed piece) while the CALLER's own save state
   * had already moved on to the new id — three genuinely divergent sources
   * of truth (save, this class's internal map, and the visible mesh) until
   * the async load eventually resolved and self-healed it. Checking the
   * cache first makes this method a true no-op (nothing touched at all)
   * whenever the replacement isn't ready, matching its own doc comment for
   * real now.
   */
  equip(slot: WearableSlot, id: WearableId | null): void {
    if (id === null) {
      const current = this.equipped[slot];
      if (current) {
        const prev = this.wearableCache.get(current);
        if (prev) this.group.remove(prev.instance.group);
      }
      delete this.equipped[slot];
      return;
    }
    const next = this.wearableCache.get(id);
    if (!next) return; // not loaded yet — main.ts re-calls once cacheWearableAsset resolves; the current piece (if any) is left exactly as it was
    const current = this.equipped[slot];
    if (current && current !== id) {
      const prev = this.wearableCache.get(current);
      if (prev) this.group.remove(prev.instance.group);
    }
    next.instance.group.visible = true;
    this.group.add(next.instance.group);
    this.equipped[slot] = id;
  }

  getEquipped(slot: WearableSlot): WearableId | null {
    return this.equipped[slot] ?? null;
  }

  /** Called every tick with the capsule's own live position/facing/state — this class never drives movement, only follows it (see this class's own header comment). */
  update(dt: number, position: THREE.Vector3, facingYaw: number, state: PlayerVisualState): void {
    this.group.position.copy(position);
    // R-1 / L2: chr_runner.glb is authored facing +X; the engine's yaw
    // convention (this method's own `facingYaw` param, per controller.ts's
    // `Math.atan2(dirX, dirZ)`) is +Z-forward — see core/assets.ts's
    // CHARACTER_FORWARD_YAW_OFFSET for the full derivation.
    this.group.rotation.y = facingYaw + CHARACTER_FORWARD_YAW_OFFSET;
    if (!this.instance) return;
    this.instance.mixer.update(dt);
    // Freshen matrixWorld immediately after the mixer moves bones — wearable
    // attachment (below) reads bone world positions THIS SAME tick, and
    // three.js doesn't recompute matrixWorld until the next render pass on
    // its own. Same staleness class as Blender's own `view_layer.update()`
    // gotcha from the Track P pipeline (3D\PLAN-3D.md gotcha #93a), applied
    // here to three.js's `AnimationMixer` instead.
    this.group.updateMatrixWorld(true);
    // Track how many CONSECUTIVE ticks grounded has read false — see this
    // field's own comment for why it's this, not a dwell-in-current-clip
    // timer (a self-caught bug: the latter lets a flicker straight through
    // the instant a player has already been running/idling for a couple
    // seconds, since dwell just keeps growing).
    if (state.grounded) this.ungroundedStreak = 0;
    else this.ungroundedStreak += 1;

    // R-3: a vault branch, ABOVE the grounded checks — there is no vault
    // clip in the asset (C10's own NOTES.md: `special_vault` was deliberately
    // omitted to keep the package focused), and 'jump' is the only one whose
    // pose reads as leaving the ground (a vault used to fall through to the
    // `state.moving` check and play 'idle' mid-vault).
    let next: PlayerClip;
    if (state.sliding) next = 'slide';
    else if (state.vaulting) next = 'jump';
    else if (!state.grounded) next = 'jump';
    else next = groundedClipFor(state);

    // Hysteresis: absorb a single-frame `grounded` flicker (a shallow slope
    // can flip it false for one tick — controller.ts's own T1 3c comment)
    // rather than snapping to 'jump' and immediately snapping back. Only
    // gates the JUMP transition, and never while genuinely vaulting (a real,
    // immediate state change, not flicker) — sliding already takes priority
    // above this branch entirely, so `next==='jump'` here already guarantees
    // `state.sliding` is false THIS tick.
    //
    // Review fix (MEDIUM, correctness): the fallback recomputes what the
    // pose should be from THIS TICK's own live state — it does NOT reuse
    // `this.currentClip` (an earlier draft did, and a 3-lens review caught
    // the gap: if `state.sliding`/`state.moving` ALSO changed on the exact
    // tick a flicker happens — e.g. a slide's own timer expires the same
    // tick a slope flicker fires — reusing the stale currentClip could
    // extend an already-ended pose ('slide') by one extra tick instead of
    // showing the genuinely-current one. Recomputing from live state can't
    // have this bug by construction — `groundedClipFor` keeps this branch
    // and the primary one above from EVER disagreeing about what "the
    // current live state's own resting pose" means.
    if (next === 'jump' && !state.vaulting && this.currentClip !== 'jump' && this.ungroundedStreak < MIN_UNGROUNDED_TICKS_BEFORE_JUMP) {
      next = groundedClipFor(state);
    }

    // C10 wire-in: walk/run both couple their timeScale to the controller's
    // true commanded speed, each against its OWN authored ground speed — run's
    // ceiling of 1.0 is Wave R's hard law and IS genuinely load-bearing, not
    // just defensive (see clipTimeScaleFor's own comment on the slide-end
    // exception a review caught); walk's ceiling is its own natural bound at
    // the switch boundary. idle/jump/slide always play at their authored rate.
    const timeScale =
      next === 'run' ? clipTimeScaleFor(state.speed, RUN_AUTHORED_SPEED, 1) :
      next === 'walk' ? clipTimeScaleFor(state.speed, WALK_AUTHORED_SPEED, WALK_TIMESCALE_CEILING) :
      1;
    this.playClip(next, timeScale);

    this.updateWearableTransforms();
  }

  private playClip(name: PlayerClip, timeScale = 1): void {
    if (!this.instance) return;
    const clip = this.instance.clips.get(name);
    if (!clip) return; // manifest guarantees all 5 exist for chr_runner — defensive only, matches every other GLB consumer's own playClip() no-op-on-missing convention
    if (this.currentClip === name) {
      // Already playing — just refresh timeScale so the per-tick walk/run
      // speed coupling doesn't have to re-trigger a crossfade every frame.
      if (this.currentAction) this.currentAction.timeScale = timeScale;
      return;
    }
    const action = this.instance.mixer.clipAction(clip);
    action.reset();
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.enabled = true;
    action.setEffectiveWeight(1);
    action.timeScale = timeScale;
    // R-3: crossfade instead of a hard `stopAllAction()` cut (which always
    // restarted the new clip's pose at frame 0, snapping visibly). Shorter
    // fade into 'jump'/'slide' so those still feel snappy; longer back into
    // 'idle' so a stop settles instead of popping.
    const fadeDuration = name === 'jump' || name === 'slide' ? 0.08 : name === 'idle' ? 0.15 : 0.12;
    if (this.currentAction) action.crossFadeFrom(this.currentAction, fadeDuration, false);
    action.play();
    this.currentAction = action;
    this.currentClip = name;
  }

  private updateWearableTransforms(): void {
    for (const slot of ['head', 'torso', 'back'] as const) {
      const id = this.equipped[slot];
      if (!id) continue;
      const eq = this.wearableCache.get(id);
      if (!eq) continue;
      const anchorNode = slot === 'head' ? this.headSocketNode : this.spineBoneNode;
      if (!anchorNode) continue;
      anchorNode.getWorldPosition(this.scratchWorld);
      this.group.worldToLocal(this.scratchWorld); // now expressed in `group`'s own local space, matching where the wearable is parented
      eq.instance.group.position.copy(this.scratchWorld).sub(eq.socketLocal);
    }
  }
}
